import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { Calendar, ChevronDown, ChevronUp, Clock, User } from "lucide-react";
import { useState } from "react";
import {
  formatDateTime,
  formatDuration,
  buildFamillesInbasket,
  categorieColors,
  categorieLabels,
} from "@repo/utils";
import referentielEnedis from "../../../../../referentiel_enedis.json";

interface InbasketMailData {
  couleur?: string;
  statut: string;
  transfertA?: string;
  important?: boolean;
  estEnBrouillon?: boolean;
  annotations?: unknown;
  reponse?: string;
}

interface InbasketResultatLLM {
  jobId: string;
  app: string;
  timestamp: number;
  data: unknown;
}

interface InbasketDonnees {
  type: "inbasket";
  template?: string;
  statutDetail: string;
  mailSelectionneId?: string;
  statutsDocuments: Record<string, string>;
  reponses: Record<string, InbasketMailData>;
  resultats?: InbasketResultatLLM[];
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface InbasketResultsProps {
  donnees: InbasketDonnees;
  candidatInfo: CandidatInfo;
}

const MAIL_TITRES: Record<string, string> = {
  achat_chaudiere: "Achat Chaudière",
  appel_personnel: "Appel personnel",
  confirmation_date_de_reunion_Plastitoc: "Confirmation date de réunion Plastitoc",
  conges_a_venir: "Congés à venir",
  contrats_de_vente: "Contrats de Vente",
  demande_conges: "Demande de congés",
  demande_formation_licence_professionnelle_rh: "Demande de Formation – Licence professionnelle RH",
  dematerialisation_documentation_technique: "Dématérialisation de la documentation technique",
  devis_construction_atelier_production: "Devis construction atelier de production",
  extension_de_l_atelier_de_production: "Extension de l'atelier de Production",
  horaires_monsieur_murat: "Horaires de Monsieur MURAT",
  invitation_conference_grgu_Nancy: "Invitation Conférence Université",
  journee_management_distance: "Journée Management à distance",
  le_teletravail_ca_a_des_limites: "Le télétravail, ça a DES LIMITES !",
  litige_societe_relax: "Litige Société RELAX",
  mega_concert: "MEGA CONCERT",
  mise_en_place_demarche_rse: "Mise en place Démarche RSE",
  offre_abonnement_echos: "Offre d'abonnement aux Echos",
  preparation_point_visio: "Préparation de notre point en visio.",
  projet_demarche_rse: "Projet démarche RSE (Responsabilité Sociétale des Entreprises)",
  projet_e_sytel: "Projet e-Sytel",
  projet_informatique: "Projet Informatique",
  projet_rse: "Projet RSE – questionnaire RSE de vos clients",
  refus_dematerialisation_documentation_technique: "Refus dématérialisation de la documentation technique",
  renouvellement_abonnement: "Renouvellement d'abonnement",
  retard_structure_atelier_de_production: "Retard structure atelier de production",
  situation_plus_que_critique: "Situation plus que critique",
  systeme_securite_informatique_passoire: "Votre système de sécurité informatique est une passoire",
  teletravail_service_administratif: "Mise en place du télétravail au sein du service administratif",
  urgent_erreur_de_livraison: "URGENT - erreur de livraison",
};

export function InbasketResults({
  donnees,
  candidatInfo,
}: InbasketResultsProps) {
  const [reponsesOpen, setReponsesOpen] = useState(false);
  const [scoresOpen, setScoresOpen] = useState(false);

  if (donnees.type !== "inbasket") {
    return <div>Type de données incorrect</div>;
  }

  // const getStatutMailBadge = (statut: string) => {
  //   switch (statut) {
  //     case StatutMailEnum.REPONDU:
  //       return <Badge variant="success">Répondu</Badge>;
  //     case StatutMailEnum.LU:
  //       return <Badge variant="warning">Lu</Badge>;
  //     default:
  //       return <Badge variant="secondary">Non lu</Badge>;
  //   }
  // };

  // const getCouleurLabel = (couleur?: string) => {
  //   switch (couleur) {
  //     case "rouge":
  //       return <Badge className="bg-red-500">Rouge - Urgent</Badge>;
  //     case "orange":
  //       return <Badge className="bg-orange-500">Orange - Important</Badge>;
  //     case "jaune":
  //       return <Badge className="bg-yellow-500">Jaune - À traiter</Badge>;
  //     case "vert":
  //       return <Badge className="bg-green-500">Vert - Information</Badge>;
  //     default:
  //       return null;
  //   }
  // };

  // const mailsList = Object.entries(donnees.reponses);
  // const totalMails = getInbasketMailsRepondables(donnees.template);
  // const documentsList = Object.entries(donnees.statutsDocuments);

  const latestResultat = donnees.resultats?.[donnees.resultats.length - 1];
  const parsedData = latestResultat?.data
    ? (() => {
        try {
          return JSON.parse(latestResultat.data as string);
        } catch {
          return latestResultat.data;
        }
      })()
    : undefined;

  const aggregatedResults = (
    parsedData?.result as
      | {
          aggregated_results?: {
            client_results_grades?: Record<string, string>;
            client_results?: Record<string, number>;
          };
        }
      | undefined
  )?.aggregated_results;

  const clientResults = aggregatedResults?.client_results_grades;
  const clientScores = aggregatedResults?.client_results;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Évaluation In-Basket</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {latestResultat && (
        <div className="px-4 md:px-6 pb-2 text-xs text-muted-foreground">
          Dernière analyse : {formatDateTime(latestResultat.timestamp)}
        </div>
      )}

      {/* Card Résultats bruts */}
      {/* <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Résultats bruts</CardTitle>
          <CardDescription>Données brutes de l'évaluation</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="space-y-6"> */}
      {/* Statut détaillé */}
      {/* <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Statut final de l'exercice
              </p>
              <p className="font-medium">{donnees.statutDetail}</p>
            </div> */}

      {/* Résumé */}
      {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {totalMails}
                </div>
                <div className="text-sm text-blue-600/80">Mails</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {
                    mailsList.filter(
                      ([, m]) => m.statut === StatutMailEnum.REPONDU,
                    ).length
                  }
                </div>
                <div className="text-sm text-green-600/80">Répondus</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {
                    mailsList.filter(([, m]) => m.statut === StatutMailEnum.LU)
                      .length
                  }
                </div>
                <div className="text-sm text-yellow-600/80">
                  Lus non répondus
                </div>
              </div>
              <div className="text-center p-4 bg-gray-100 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">
                  {
                    mailsList.filter(
                      ([, m]) => m.statut === StatutMailEnum.NON_LU,
                    ).length
                  }
                </div>
                <div className="text-sm text-gray-500">Non lus</div>
              </div>
            </div> */}

      {/* Liste des mails */}
      {/* <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Détail des mails
              </h3>
              <div className="space-y-3">
                {mailsList.map(([mailId, mailData]) => (
                  <div
                    key={mailId}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-muted-foreground">
                          {mailId}
                        </span>
                        {getStatutMailBadge(mailData.statut)}
                        {mailData.important && (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                      {getCouleurLabel(mailData.couleur)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      {mailData.transfertA && (
                        <div>
                          <span className="text-muted-foreground">
                            Transféré à:
                          </span>
                          <p className="font-medium">{mailData.transfertA}</p>
                        </div>
                      )}
                      {mailData.estEnBrouillon && (
                        <div>
                          <Badge variant="outline">En brouillon</Badge>
                        </div>
                      )}
                    </div>

                    {mailData.reponse && (
                      <div className="mt-3 p-3 bg-muted rounded">
                        <p className="text-sm font-medium text-muted-foreground mb-1">
                          Réponse:
                        </p>
                        <p className="whitespace-pre-wrap text-sm">
                          {mailData.reponse}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div> */}

      {/* Documents consultés */}
      {/* <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents consultés
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {documentsList.map(([docId, statut]) => (
                  <div
                    key={docId}
                    className="p-3 border border-gray-200 rounded-lg flex items-center justify-between"
                  >
                    <span className="font-mono text-sm">{docId}</span>
                    {statut === StatutDocumentEnum.LU ? (
                      <Badge variant="success">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Lu
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Non lu</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Card Scores de compétences */}
      {clientResults && (
        <Card className="mb-6 w-full overflow-hidden">
          <CardHeader>
            <CardTitle>Scores de compétences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                {(["A", "B", "C", "D"] as const).map((cat) => (
                  <div key={cat} className="flex items-center gap-2 text-sm">
                    <span
                      className="inline-block h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: categorieColors[cat] }}
                    />
                    <span className="text-muted-foreground">
                      {categorieLabels[cat] ?? cat}
                    </span>
                  </div>
                ))}
              </div>
              {buildFamillesInbasket(clientResults, referentielEnedis).map(
                (famille) => (
                  <div
                    key={famille.nom}
                    className="rounded-lg border overflow-hidden"
                  >
                    <table className="w-full text-sm">
                      <thead>
                        <tr
                          style={{
                            backgroundColor: `color-mix(in srgb, ${famille.color} 10%, transparent)`,
                          }}
                        >
                          <th
                            colSpan={2}
                            className="px-4 py-3 text-left font-bold"
                            style={{ color: famille.color }}
                          >
                            {famille.nom}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {famille.aptitudes.map((aptitude) => (
                          <tr
                            key={aptitude.nom}
                            className="border-t border-border hover:bg-muted/40 transition-colors"
                          >
                            <td className="px-4 py-3 font-medium text-left w-2/3">
                              {aptitude.nom}
                            </td>
                            <td className="px-4 py-3 text-center w-1/3">
                              <span
                                className="inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-white w-full max-w-40"
                                style={{
                                  backgroundColor:
                                    categorieColors[aptitude.categorie] ??
                                    "#6b7280",
                                }}
                              >
                                {categorieLabels[aptitude.categorie] ??
                                  aptitude.categorie}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ),
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scores numériques client_results */}
      {clientScores && Object.keys(clientScores).length > 0 && (
        <Card className="mb-6 w-full overflow-hidden">
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setScoresOpen((o) => !o)}
          >
            <div className="flex items-center justify-between">
              <CardTitle>Scores détaillés</CardTitle>
              {scoresOpen ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          {scoresOpen && (
            <CardContent>
              <div className="space-y-2">
                {Object.entries(clientScores).map(([competence, score]) => (
                  <div
                    key={competence}
                    className="flex items-center justify-between rounded-lg border border-border px-4 py-3 text-sm"
                  >
                    <span className="font-medium">{competence}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {score.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Réponses du candidat */}
      {(() => {
        const reponsesAvecTexte = Object.entries(donnees.reponses).filter(
          ([, m]) => m.statut === "repondu" && m.reponse,
        );
        if (reponsesAvecTexte.length === 0) return null;
        return (
          <Card className="mb-6 w-full overflow-hidden">
            <CardHeader
              className="cursor-pointer select-none"
              onClick={() => setReponsesOpen((o) => !o)}
            >
              <div className="flex items-center justify-between">
                <CardTitle>Réponses du candidat ({reponsesAvecTexte.length})</CardTitle>
                {reponsesOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
            {reponsesOpen && (
              <CardContent>
                <div className="space-y-4">
                  {reponsesAvecTexte.map(([cleMail, mailData]) => (
                    <div key={cleMail} className="rounded-lg border border-border p-4">
                      <p className="mb-2 font-medium">
                        {MAIL_TITRES[cleMail] ?? cleMail}
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                        {mailData.reponse}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })()}
    </div>
  );
}
